import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Collapsible from "../reuse/Collapsible";
import { DisplaySection, Loader, Section, SpinnerIcon } from "../Styles";
import Confirmation from "./Confirmation";
import { filesToBase64 } from "../../utils/filesToBase64";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useDataContext } from "../../context/DataContext";
import FileUploadMessage from "./FileUploadMessage";
import { DocumentTypes, genericErrorMessage } from "../../constants";
import DocumentLists from "./DocumentLists";
import FileUploader from "../FileUpload/FileUploader";

const MainWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  /* gap: 20px; */
  padding-bottom: 20px;
  margin-top: -18px;
`;

const CenterText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px;
`;

const TableList = styled.div`
  table {
    border: 1px solid ${({ theme }) => theme.branding.borderColor};
    overflow: auto;
  }
`;

const IdentificationWrapper = styled.div`
  flex: 1;
  padding: 15px;
  border-radius: 5px;
`;

const IdentificationWrapperA = styled(IdentificationWrapper)`
  max-width: 700px;
  background-color: ${({ theme }) => theme.branding.voilet};
`;

const IdentificationWrapperB = styled(IdentificationWrapper)`
  flex: 2;
  background-color: ${({ theme }) => theme.branding.lightBlue};
`;

function getInitialState(requestRecord) {
  const uploads = {};
  if (requestRecord?.requireIdentification_F) {
    uploads.requestorIDGroupAUpload_F = {
      fid: "requestorIDGroupAUpload_F",
      fileExists: false,
    };

    uploads.requestorIDGroupBUpload_F = {
      fid: "requestorIDGroupBUpload_F",
      fileExists: false,
    };

    uploads.requestorIDGroupCUpload_F = {
      fid: "requestorIDGroupCUpload_F",
      fileExists: false,
    };
  }

  if (requestRecord?.requirePoA_F) {
    uploads.poaUpload_F = {
      fid: "poaUpload_F",
      fileExists: false,
    };
  }

  if (requestRecord?.proofGuardianship_F) {
    uploads.proofGuardianshipUpload_F = {
      fid: "proofGuardianshipUpload_F",
      fileExists: false,
    };
  }

  if (requestRecord?.immTracDIRConsent_F) {
    uploads.immDIRConsentUpload_F = {
      fid: "immDIRConsentUpload_F",
      fileExists: false,
    };
  }

  if (requestRecord?.immTracConsentForm_F) {
    uploads.immTracConsentFormUpload_F = {
      fid: "immTracConsentFormUpload_F",
      fileExists: false,
    };
  }

  if (requestRecord?.immTracConsentForm_F) {
    uploads.minorConsentUpload_F = {
      fid: "minorConsentUpload_F",
      fileExists: false,
    };
  }

  if (requestRecord?.additionalDocument_F) {
    uploads.additionalDocumentUpload_F = {
      fid: "additionalDocumentUpload_F",
      fileExists: false,
    };
  }

  return uploads;
}

export default function Documentation({ requestRecord }) {
  const navigate = useNavigate();

  const [isSaving, setIsSaving] = useState(false);
  const axiosPrivate = useAxiosPrivate();

  const { setData } = useDataContext();

  const initialUploadState = getInitialState(requestRecord);

  const [fileUploads, setFileUploads] = useState();

  const [documents, setDocuments] = useState();

  useEffect(() => {
    const fids = Object.keys(initialUploadState);

    if (fids.length > 0 && requestRecord) {
      const uploads = {};

      fids.forEach((fid) => {
        const findDocuments = requestRecord.documents.findIndex(
          (x) => x.type_F === DocumentTypes[fid],
        );
        if (findDocuments > -1) {
          uploads[fid] = {
            fid,
            fileExists: true,
          };
        } else {
          uploads[fid] = {
            fid,
            fileExists: false,
          };
        }
      });

      setFileUploads(uploads);

      setDocuments(requestRecord.documents);
    }
  }, [requestRecord]);

  const handleUploadFile = useCallback(
    async (files, type, fid) => {
      try {
        if (files) {
          const fileSizeIndex = [...files].findIndex(
            (x) => x.size / (1024 * 1024) > 30,
          );
          if (fileSizeIndex >= 0) {
            toast.error(
              "Please upload file less than 30 MB  | Cargue un archivo de menos de 30 MB",
            );
            return;
          }

          const base64Files = await filesToBase64(files);

          const fileToUpload = [];
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < base64Files.length; i++) {
            fileToUpload.push({
              id_F: `temp_${Date.now()}-${Math.random().toString(36).substring(2, 15)}`,
              relatedRequest_F: requestRecord.id_F,
              type_F: type,
              file: base64Files[i].base64,
              fileName: base64Files[i].name,
            });
          }

          setDocuments((prevItems) => [...prevItems, ...fileToUpload]);

          setFileUploads((prevState) => ({
            ...prevState,
            [fid]: {
              ...prevState[fid],
              fileExists: true,
            },
          }));
        } else {
          toast.error(
            "Please upload a PDF or Image file | Cargue un archivo PDF o una imagen",
          );
        }
      } catch (error) {
        toast.error("Error uploading file | Error al cargar el archivo");
      }
    },
    [documents],
  );

  const handleRemoveFile = useCallback(
    async (rid, fid) => {
      try {
        setFileUploads((prevState) => ({
          ...prevState,
          [fid]: {
            ...prevState[fid],
            isUploading: true,
          },
        }));

        const indexToRemove = documents.findIndex((x) => x.id_F === rid);

        if (!documents[indexToRemove]?.file) {
          // Delete file
          const payload = {
            rid,
          };
          await axiosPrivate.post(`request/removeFile`, payload);
        }

        const remainingFiles = documents.filter(
          (x) => x.type_F === DocumentTypes[fid],
        );

        if (remainingFiles.length <= 1) {
          setFileUploads((prevState) => ({
            ...prevState,
            [fid]: {
              ...prevState[fid],
              fileExists: false,
            },
          }));
        }

        setDocuments((prevItems) =>
          prevItems.filter((_, index) => index !== indexToRemove),
        );
      } catch (error) {
        toast.error("Error removing file | Error al eliminar el archivo");
      } finally {
        setFileUploads((prevState) => ({
          ...prevState,
          [fid]: {
            ...prevState[fid],
            isUploading: false,
          },
        }));
      }
    },
    [documents],
  );

  const shouldDisableSubmit = () => {
    const fids = Object.keys(initialUploadState);

    if (fids.length > 0) {
      let disabled = false;
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < fids.length; i++) {
        const fid = fids[i];

        if (
          requestRecord?.requireIdentification_F &&
          (fid === "requestorIDGroupAUpload_F" ||
            fid === "requestorIDGroupBUpload_F" ||
            fid === "requestorIDGroupCUpload_F")
        ) {
          if (!fileUploads?.requestorIDGroupAUpload_F?.fileExists) {
            if (
              !fileUploads?.requestorIDGroupBUpload_F?.fileExists ||
              !fileUploads?.requestorIDGroupCUpload_F?.fileExists
            ) {
              disabled = true;
              break;
            }
          }

          if (
            !fileUploads?.requestorIDGroupBUpload_F?.fileExists &&
            !fileUploads?.requestorIDGroupCUpload_F?.fileExists
          ) {
            if (!fileUploads?.requestorIDGroupAUpload_F?.fileExists) {
              disabled = true;
              break;
            }
          }
        } else if (fid === "immTracConsentFormUpload_F") {
          if (requestRecord?.age_F >= 18 && !fileUploads[fid]?.fileExists) {
            disabled = true;
            break;
          }
        } else if (fid === "minorConsentUpload_F") {
          if (requestRecord.age_F < 18 && !fileUploads[fid]?.fileExists) {
            disabled = true;
            break;
          }
        } else if (!fileUploads[fid]?.fileExists) {
          disabled = true;
          break;
        }
      }

      return disabled;
    }

    return true;
  };

  const handleSaveLater = useCallback(async () => {
    try {
      setIsSaving(true);

      // only upload object with file attributes
      const payload = {
        uploads: documents.filter((x) => x.file),
      };

      const response = await axiosPrivate.post(
        `request/v2/upload/${requestRecord.id_F}`,
        payload,
      );

      // Update context state
      if (response.data.length > 0) {
        setData((prevItems) =>
          prevItems.map((item) =>
            item.id_F === requestRecord.id_F
              ? { ...item, ...response.data[0] }
              : item,
          ),
        );
      }

      navigate("/requests");
    } catch (err) {
      toast.error(genericErrorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [documents]);

  const handleSubmitDocumentation = useCallback(async () => {
    try {
      setIsSaving(true);

      const payload = {
        uploads: documents.filter((x) => x.file),
        fields: {
          signature_F: { value: 1 },
          status_F: { value: "In Review" },
          signedAuthorization_F: {
            value: `</br></br></br><i>Electronically Signed by ${requestRecord?.firstName_F} ${requestRecord?.lastName_F} (${requestRecord?.requestorEmail_F}) on ${new Date().toString()}</i>`,
          },
        },
      };

      const response = await axiosPrivate.post(
        `request/v2/upload/${requestRecord.id_F}`,
        payload,
      );

      // Update context state
      if (response.data.length > 0) {
        setData((prevItems) =>
          prevItems.map((item) =>
            item.id_F === requestRecord.id_F
              ? { ...item, ...response.data[0] }
              : item,
          ),
        );
      }

      navigate("/requests");
    } catch (err) {
      toast.error(genericErrorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [documents]);

  return (
    fileUploads && (
      <>
        {isSaving && (
          <Loader>
            <SpinnerIcon size={60} />
          </Loader>
        )}
        <MainWrapper>
          {requestRecord?.requireIdentification_F && (
            <Collapsible title="Identification | Identificación">
              <DisplaySection style={{ marginBottom: "10px" }}>
                {!fileUploads?.requestorIDGroupBUpload_F?.fileExists &&
                  !fileUploads?.requestorIDGroupCUpload_F?.fileExists && (
                    <IdentificationWrapperA>
                      <FileUploader
                        isUploading={
                          fileUploads?.requestorIDGroupAUpload_F?.isUploading
                        }
                        type={DocumentTypes.requestorIDGroupAUpload_F}
                        fid="requestorIDGroupAUpload_F"
                        handleUploadFile={handleUploadFile}
                      />
                      <DocumentLists
                        documents={documents}
                        type={DocumentTypes.requestorIDGroupAUpload_F}
                        fid="requestorIDGroupAUpload_F"
                        handleRemoveFile={handleRemoveFile}
                      />

                      <TableList
                        dangerouslySetInnerHTML={{
                          __html: requestRecord?.requestorIDGroupAUploadText_F,
                        }}
                      />
                    </IdentificationWrapperA>
                  )}

                {!fileUploads?.requestorIDGroupAUpload_F?.fileExists &&
                  !fileUploads?.requestorIDGroupBUpload_F?.fileExists &&
                  !fileUploads?.requestorIDGroupCUpload_F?.fileExists && (
                    <CenterText>
                      <b>OR</b>
                    </CenterText>
                  )}

                {!fileUploads?.requestorIDGroupAUpload_F?.fileExists && (
                  <IdentificationWrapperB>
                    <DisplaySection>
                      <div style={{ flex: 1 }}>
                        <FileUploader
                          isUploading={
                            fileUploads?.requestorIDGroupBUpload_F?.isUploading
                          }
                          type={DocumentTypes.requestorIDGroupBUpload_F}
                          fid="requestorIDGroupBUpload_F"
                          handleUploadFile={handleUploadFile}
                        />
                        <DocumentLists
                          documents={documents}
                          type={DocumentTypes.requestorIDGroupBUpload_F}
                          fid="requestorIDGroupBUpload_F"
                          handleRemoveFile={handleRemoveFile}
                        />
                      </div>
                      <CenterText>
                        <b>AND</b>
                      </CenterText>
                      <div style={{ flex: 1 }}>
                        <FileUploader
                          isUploading={
                            fileUploads?.requestorIDGroupCUpload_F?.isUploading
                          }
                          type={DocumentTypes.requestorIDGroupCUpload_F}
                          fid="requestorIDGroupCUpload_F"
                          handleUploadFile={handleUploadFile}
                        />
                        <DocumentLists
                          documents={documents}
                          type={DocumentTypes.requestorIDGroupCUpload_F}
                          fid="requestorIDGroupCUpload_F"
                          handleRemoveFile={handleRemoveFile}
                        />
                      </div>
                    </DisplaySection>

                    <TableList
                      dangerouslySetInnerHTML={{
                        __html: requestRecord?.requestorIDGroupBCUploadText_F,
                      }}
                    />
                  </IdentificationWrapperB>
                )}
              </DisplaySection>
            </Collapsible>
          )}

          {fileUploads?.poaUpload_F && (
            <Collapsible title="Power of Attorney | Poder Notarial">
              <DisplaySection style={{ marginBottom: "10px" }}>
                <Section>
                  <FileUploader
                    isUploading={fileUploads?.poaUpload_F?.isUploading}
                    type={DocumentTypes.poaUpload_F}
                    fid="poaUpload_F"
                    handleUploadFile={handleUploadFile}
                  />
                  <DocumentLists
                    documents={documents}
                    type={DocumentTypes.poaUpload_F}
                    fid="poaUpload_F"
                    handleRemoveFile={handleRemoveFile}
                  />
                </Section>
                <Section style={{ flex: 2 }}>
                  <FileUploadMessage message={requestRecord.poaMessage_F} />
                </Section>
              </DisplaySection>
            </Collapsible>
          )}

          {fileUploads?.proofGuardianshipUpload_F && (
            <Collapsible title="Proof of Guardianship | Prueba de Tutela">
              <DisplaySection style={{ marginBottom: "10px" }}>
                <Section>
                  <FileUploader
                    isUploading={
                      fileUploads?.proofGuardianshipUpload_F?.isUploading
                    }
                    type={DocumentTypes.proofGuardianshipUpload_F}
                    fid="proofGuardianshipUpload_F"
                    handleUploadFile={handleUploadFile}
                  />
                  <DocumentLists
                    documents={documents}
                    type={DocumentTypes.proofGuardianshipUpload_F}
                    fid="proofGuardianshipUpload_F"
                    handleRemoveFile={handleRemoveFile}
                  />
                </Section>
                <Section style={{ flex: 2 }}>
                  <FileUploadMessage
                    message={requestRecord.proofGuardianshipMessaage_F}
                  />
                </Section>
              </DisplaySection>
            </Collapsible>
          )}

          {fileUploads?.immDIRConsentUpload_F && (
            <Collapsible title="Disaster Information Retention Consent Form | Consentimiento para la retención de información">
              <DisplaySection style={{ marginBottom: "10px" }}>
                <Section>
                  <FileUploader
                    isUploading={
                      fileUploads?.immDIRConsentUpload_F?.isUploading
                    }
                    type={DocumentTypes.immDIRConsentUpload_F}
                    fid="immDIRConsentUpload_F"
                    handleUploadFile={handleUploadFile}
                  />
                  <DocumentLists
                    documents={documents}
                    type={DocumentTypes.immDIRConsentUpload_F}
                    fid="immDIRConsentUpload_F"
                    handleRemoveFile={handleRemoveFile}
                  />
                </Section>
                <Section style={{ flex: 2 }}>
                  <FileUploadMessage
                    message={requestRecord.immDIRConsentMessage_F}
                  />
                </Section>
              </DisplaySection>
            </Collapsible>
          )}

          {fileUploads?.immTracConsentFormUpload_F &&
            requestRecord.age_F >= 18 && (
              <Collapsible title="Adult Consent Form | Consentimiento para Adultos">
                <DisplaySection style={{ marginBottom: "10px" }}>
                  <Section>
                    <FileUploader
                      isUploading={
                        fileUploads?.immTracConsentFormUpload_F?.isUploading
                      }
                      type={DocumentTypes.immTracConsentFormUpload_F}
                      fid="immTracConsentFormUpload_F"
                      handleUploadFile={handleUploadFile}
                    />
                    <DocumentLists
                      documents={documents}
                      type={DocumentTypes.immTracConsentFormUpload_F}
                      fid="immTracConsentFormUpload_F"
                      handleRemoveFile={handleRemoveFile}
                    />
                  </Section>
                  <Section style={{ flex: 2 }}>
                    <FileUploadMessage
                      message={requestRecord.immTracConsentFormMessage_F}
                    />
                  </Section>
                </DisplaySection>
              </Collapsible>
            )}

          {fileUploads?.minorConsentUpload_F && requestRecord.age_F < 18 && (
            <Collapsible title="Minor Consent Form | Consentimiento para menores de edad">
              <DisplaySection style={{ marginBottom: "10px" }}>
                <Section>
                  <FileUploader
                    isUploading={fileUploads?.minorConsentUpload_F?.isUploading}
                    type={DocumentTypes.minorConsentUpload_F}
                    fid="minorConsentUpload_F"
                    handleUploadFile={handleUploadFile}
                  />
                  <DocumentLists
                    documents={documents}
                    type={DocumentTypes.minorConsentUpload_F}
                    fid="minorConsentUpload_F"
                    handleRemoveFile={handleRemoveFile}
                  />
                </Section>
                <Section style={{ flex: 2 }}>
                  <FileUploadMessage
                    message={requestRecord.minorConsentMessage_F}
                  />
                </Section>
              </DisplaySection>
            </Collapsible>
          )}

          {fileUploads?.additionalDocumentUpload_F && (
            <Collapsible title="Additional Documentation | Documentación Adicional">
              <DisplaySection style={{ marginBottom: "10px" }}>
                <Section>
                  <FileUploader
                    isUploading={
                      fileUploads?.additionalDocumentUpload_F?.isUploading
                    }
                    type={DocumentTypes.additionalDocumentUpload_F}
                    fid="additionalDocumentUpload_F"
                    handleUploadFile={handleUploadFile}
                  />
                  <DocumentLists
                    documents={documents}
                    type={DocumentTypes.additionalDocumentUpload_F}
                    fid="additionalDocumentUpload_F"
                    handleRemoveFile={handleRemoveFile}
                  />
                </Section>
                <Section style={{ flex: 2 }}>
                  <FileUploadMessage
                    message={requestRecord.additionalDocumentMessage_F}
                  />
                </Section>
              </DisplaySection>
            </Collapsible>
          )}

          <Confirmation
            requestRecord={requestRecord}
            disableSubmit={shouldDisableSubmit()}
            handleSubmitDocumentation={handleSubmitDocumentation}
            handleSaveLater={handleSaveLater}
          />
        </MainWrapper>
      </>
    )
  );
}
